import { useState, useEffect, useRef } from 'react';

// Searchable, sorted PO picker — the finance/procurement equivalent of SowSelect,
// so choosing a purchase order is a type-to-filter box instead of a long scroll.
// Tolerant of both field names in use across the app (po_number and reference)
// and shows the supplier alongside.

const poLabel = (p) => p?.po_number || p?.reference || p?.id || '';
const poSub   = (p) => p?.supplier_name || '';

export default function PoSelect({ pos = [], value, onChange, placeholder = '— Select PO —', disabled, inputClassName }) {
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const sorted = [...pos].sort((a, b) => poLabel(a).localeCompare(poLabel(b)));
  const selected = value ? sorted.find(p => String(p.id) === String(value)) : null;

  const filtered = search
    ? sorted.filter(p =>
        poLabel(p).toLowerCase().includes(search.toLowerCase()) ||
        poSub(p).toLowerCase().includes(search.toLowerCase())
      )
    : sorted;

  useEffect(() => {
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setSearch(''); }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  useEffect(() => { if (open && !selected) inputRef.current?.focus(); }, [open, selected]);

  const pick = (id) => { onChange(String(id)); setOpen(false); setSearch(''); };
  const clear = () => { onChange(''); setSearch(''); setOpen(true); };

  if (selected) {
    return (
      <div className={`flex items-center gap-1.5 border border-gray-200 rounded px-2 py-1 bg-white text-[9px] ${disabled ? 'opacity-50' : ''}`}>
        <span className="flex-1 truncate font-semibold text-navy">
          <span className="font-mono">{poLabel(selected)}</span>
          {poSub(selected) && <span className="font-normal text-gray-500 ml-1.5">{poSub(selected)}</span>}
        </span>
        {!disabled && (
          <button type="button" onClick={clear} title="Remove — pick a different PO"
            className="text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0 leading-none rounded p-1 -m-1 transition-colors">✕</button>
        )}
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        className={inputClassName ?? 'form-input text-[9px] w-full'}
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-0.5 border border-gray-200 rounded bg-white shadow-lg max-h-[260px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[9px] text-gray-400 italic">{search ? `No POs match "${search}"` : 'No POs available'}</div>
          ) : filtered.map(p => (
            <div key={p.id}
              onMouseDown={e => { e.preventDefault(); pick(p.id); }}
              className="px-3 py-1.5 text-[9px] cursor-pointer hover:bg-gold/10 border-b border-gray-50 last:border-0 flex items-center gap-2">
              <span className="font-semibold font-mono text-navy">{poLabel(p)}</span>
              {poSub(p) && <span className="text-gray-500 truncate">{poSub(p)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
